import { useEffect, useState } from 'react';
import { Columns3, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

/**
 * Column definition for employee table
 */
export interface ColumnDefinition {
  id: string;
  label: string;
  defaultVisible: boolean;
  alwaysVisible?: boolean; // Can't be hidden (e.g., Name, Actions)
}

/**
 * All available columns for employee table
 * Based on documentation: 01-directory-list.md
 */
export const ALL_COLUMNS: ColumnDefinition[] = [
  { id: 'name', label: 'Name', defaultVisible: true, alwaysVisible: true },
  { id: 'position', label: 'Position', defaultVisible: true },
  { id: 'department', label: 'Department', defaultVisible: true },
  { id: 'location', label: 'Location', defaultVisible: true },
  { id: 'manager', label: 'Manager', defaultVisible: true },
  { id: 'startDate', label: 'Start Date', defaultVisible: true },
  { id: 'email', label: 'Email', defaultVisible: false },
  { id: 'employmentType', label: 'Employment Type', defaultVisible: false },
  { id: 'seniorityLevel', label: 'Seniority Level', defaultVisible: false },
  { id: 'status', label: 'Status', defaultVisible: false },
  { id: 'actions', label: 'Actions', defaultVisible: true, alwaysVisible: true },
];

export const DEFAULT_VISIBLE_COLUMNS = ALL_COLUMNS
  .filter((col) => col.defaultVisible)
  .map((col) => col.id);

const STORAGE_KEY = 'employee-table-columns';

interface ColumnConfigProps {
  visibleColumns: string[];
  onColumnsChange: (columns: string[]) => void;
}

export function ColumnConfig({ visibleColumns, onColumnsChange }: ColumnConfigProps) {
  const [open, setOpen] = useState(false);

  const toggleColumn = (columnId: string) => {
    const column = ALL_COLUMNS.find((c) => c.id === columnId);
    if (column?.alwaysVisible) return;

    if (visibleColumns.includes(columnId)) {
      onColumnsChange(visibleColumns.filter((id) => id !== columnId));
    } else {
      // Add column in the correct order
      const orderedColumns = ALL_COLUMNS
        .filter((col) => visibleColumns.includes(col.id) || col.id === columnId)
        .map((col) => col.id);
      onColumnsChange(orderedColumns);
    }
  };

  const resetToDefault = () => {
    onColumnsChange(DEFAULT_VISIBLE_COLUMNS);
  };

  // Count hidden columns (excluding always visible)
  const hiddenCount = ALL_COLUMNS.filter(
    (col) => !col.alwaysVisible && !visibleColumns.includes(col.id)
  ).length;

  return (
    <TooltipProvider delayDuration={0}>
      <Popover open={open} onOpenChange={setOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon" className="relative rounded-full cursor-pointer">
                <Columns3 className="h-4 w-4" />
                {hiddenCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-muted text-[9px] font-medium text-muted-foreground">
                    {hiddenCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>Columns</TooltipContent>
        </Tooltip>
      <PopoverContent align="end" className="w-56 p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-sm font-medium">Columns</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={resetToDefault}
            className="h-7 text-xs text-muted-foreground hover:text-foreground"
          >
            Reset
          </Button>
        </div>
        <div className="max-h-[300px] overflow-y-auto p-1">
          {ALL_COLUMNS.map((column) => {
            const isVisible = visibleColumns.includes(column.id);
            const isDisabled = column.alwaysVisible;

            return (
              <div
                key={column.id}
                className={cn(
                  'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm',
                  !isDisabled && 'cursor-pointer hover:bg-muted',
                  isDisabled && 'opacity-50'
                )}
                onClick={() => !isDisabled && toggleColumn(column.id)}
              >
                <GripVertical className="h-4 w-4 text-muted-foreground/50" />
                <Checkbox
                  checked={isVisible}
                  disabled={isDisabled}
                  className="pointer-events-none"
                />
                <span className={cn(!isVisible && 'text-muted-foreground')}>
                  {column.label}
                </span>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
    </TooltipProvider>
  );
}

/**
 * Hook to manage column visibility with localStorage persistence
 */
export function useColumnVisibility() {
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    if (typeof window === 'undefined') return DEFAULT_VISIBLE_COLUMNS;

    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Validate that stored columns are valid
        const validColumns = parsed.filter((id: string) =>
          ALL_COLUMNS.some((col) => col.id === id)
        );
        // Ensure always visible columns are included
        const alwaysVisible = ALL_COLUMNS
          .filter((col) => col.alwaysVisible)
          .map((col) => col.id);
        const combined = [...new Set([...alwaysVisible, ...validColumns])];
        return combined;
      } catch {
        return DEFAULT_VISIBLE_COLUMNS;
      }
    }
    return DEFAULT_VISIBLE_COLUMNS;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  return { visibleColumns, setVisibleColumns };
}

